import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  fishprawncrabMultipliersSchema,
  STAKE_GRANULARITY_BY_GAME,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { classifyReturn } from './inheritance.js';
import { prettyGame } from '../games.js';

export interface FishPrawnCrabMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** The three figures, one per bet family. The wire field names. */
export type FpcField = 'singleWinningsPerDieBps' | 'pairReturnBps' | 'longCardReturnBps';

/** Wire shape: all three figures, every time. */
export interface FpcMultipliersValue {
  version: number;
  singleWinningsPerDieBps: number;
  pairReturnBps: number;
  longCardReturnBps: number;
}

type Family = 'SINGLE' | 'PAIR' | 'LONG';

/** Basis-point scale: bps / BPS_SCALE = multiple. */
const BPS_SCALE = 10000;

/**
 * The stake grid every chip and minimum is held to, read from the table-config
 * registry rather than typed again here.
 */
export const FPC_STAKE_GRID = STAKE_GRANULARITY_BY_GAME.fishprawncrab!;

/**
 * A figure keeps `floor(stake × bps / 10000)` exact on that grid only when it
 * is a multiple of this step, and so does every Three Soldiers return built
 * from such a per-die figure (`10000 + n × figure`). Off it, every win is
 * floored down. Same formula as RETURN_BPS_STEP in the game package, and the
 * step the multipliers schema enforces on save. 100 bps is 0.01×, which is why
 * the inputs below take a multiple to two decimals.
 */
export const FPC_STEP_BPS = BPS_SCALE / FPC_STAKE_GRID;

/**
 * Winning outcomes out of the 216 equally likely rolls of three dice, per
 * family. Mirrors WINNING_OUTCOMES in @apx/game-fishprawncrab. The dice are
 * uniform and not admin-tunable, so these are constants and each family's RTP
 * is exact. Three Soldiers is split by how many dice show the symbol, because
 * its return grows with that count.
 */
export const FPC_OUTCOMES = 216;
export const FPC_WINS = {
  SINGLE_BY_COUNT: { 1: 75, 2: 15, 3: 1 },
  SINGLE: 91,
  PAIR: 16,
  LONG: 30,
} as const;

/**
 * Built-in figures — mirrors DEFAULT_FPC_MULTIPLIERS in the game package.
 * Guarded by `multipliers-defaults.test.ts`.
 */
export const FPC_DEFAULT_MULTIPLIERS: Readonly<FpcMultipliersValue> = Object.freeze({
  version: 1,
  singleWinningsPerDieBps: 10_800,
  pairReturnBps: 129_600,
  longCardReturnBps: 69_100,
});

interface FieldSpec {
  key: FpcField;
  family: Family;
  label: string;
  /** What the figure is, under the label. */
  note: string;
}

/** One row per family, in board order. */
export const FPC_FIELDS: readonly FieldSpec[] = [
  {
    key: 'singleWinningsPerDieBps',
    family: 'SINGLE',
    label: 'Three Soldiers',
    note:
      'Winnings per die showing the symbol. The stake comes back plus this once for each such die. ' +
      `Wins on ${FPC_WINS.SINGLE} of the ${FPC_OUTCOMES} rolls: one die ${FPC_WINS.SINGLE_BY_COUNT[1]}, ` +
      `two dice ${FPC_WINS.SINGLE_BY_COUNT[2]}, all three ${FPC_WINS.SINGLE_BY_COUNT[3]}.`,
  },
  {
    key: 'pairReturnBps',
    family: 'PAIR',
    label: 'Pair',
    note: `Total return when the symbol shows on two or three dice. Wins on ${FPC_WINS.PAIR} of the ${FPC_OUTCOMES} rolls.`,
  },
  {
    key: 'longCardReturnBps',
    family: 'LONG',
    label: 'Long Card',
    note: `Total return when both symbols show. Wins on ${FPC_WINS.LONG} of the ${FPC_OUTCOMES} rolls.`,
  },
];

const FIELD_KEYS: readonly FpcField[] = FPC_FIELDS.map((f) => f.key);

/** Total return of a Three Soldiers win with `count` dice showing the symbol. */
export function fpcSingleReturnBps(perDieBps: number, count: 1 | 2 | 3): number {
  return BPS_SCALE + perDieBps * count;
}

/** Exact RTP of every bet in one family at a given figure. */
export function fpcFamilyRtp(family: Family, bps: number): number {
  if (family === 'SINGLE') {
    const byCount = FPC_WINS.SINGLE_BY_COUNT;
    const returned =
      byCount[1] * fpcSingleReturnBps(bps, 1) +
      byCount[2] * fpcSingleReturnBps(bps, 2) +
      byCount[3] * fpcSingleReturnBps(bps, 3);
    return returned / BPS_SCALE / FPC_OUTCOMES;
  }
  return (FPC_WINS[family] / FPC_OUTCOMES) * (bps / BPS_SCALE);
}

/** bps → the multiple shown in an input: two decimals on the step, four off it. */
export function fpcBpsToMultiple(bps: number): string {
  const x = bps / BPS_SCALE;
  return bps % FPC_STEP_BPS === 0 ? x.toFixed(2) : x.toFixed(4);
}

/** A multiple typed into an input → bps. Anything unreadable is 0, which the
 *  schema refuses as not positive. */
export function fpcMultipleToBps(text: string): number {
  const x = Number(text);
  return Number.isFinite(x) ? Math.round(x * BPS_SCALE) : 0;
}

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/** Human hint for a total return: bps → "12.96× return (11.96:1)". */
function returnHint(bps: number): string {
  return `${fpcBpsToMultiple(bps)}× return (${fpcBpsToMultiple(bps - BPS_SCALE)}:1)`;
}

/** Read-only returns a per-die figure settles at: "1 die 2.08× · 2 dice 3.16× · 3 dice 4.24×". */
function singleReturnsHint(perDieBps: number): string {
  return ([1, 2, 3] as const)
    .map(
      (n) =>
        `${n} ${n === 1 ? 'die' : 'dice'} <strong>${fpcBpsToMultiple(fpcSingleReturnBps(perDieBps, n))}×</strong>`,
    )
    .join(' · ');
}

/** Pull the three figures off a stored value of unknown shape. */
function pickFigures(value: unknown): Partial<Record<FpcField, number>> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const out: Partial<Record<FpcField, number>> = {};
  for (const key of FIELD_KEYS) {
    const v = (value as Record<string, unknown>)[key];
    if (typeof v === 'number') out[key] = v;
  }
  return out;
}

/**
 * Fish Prawn Crab payout-multiplier editor. ONE figure per bet family, because
 * every symbol is equally likely: the Three Soldiers winnings per die, and the
 * Pair and Long Card total returns. Each is entered as a multiple in steps of
 * 0.01 and stored as bps (1.08 = 10 800 bps).
 *
 * These figures are the game's ONLY tunable RTP surface — three uniform dice,
 * no RNG-weight lever — so each family's RTP is exact and shown live as you
 * type. Three Soldiers is count-scaled: a win returns the stake plus the per-die
 * figure once for each die showing the symbol, so its row shows the three
 * returns that figure settles at, read-only.
 *
 * ALL THREE FIGURES ARE SAVED, IN BOTH SCOPES. The schema for this game takes
 * all three or none, so unlike the editors that submit only the rows an
 * integrator changed, a save here pins every figure for that integrator at the
 * value on screen. The inherited/override tags still show where each figure on
 * screen came from, and per-row revert puts the provider's figure back in the
 * form. Changes ride the two-admin approval flow.
 */
export async function renderFishPrawnCrabMultipliersPage(
  root: HTMLElement,
  deps: FishPrawnCrabMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;
  const defaults = FPC_DEFAULT_MULTIPLIERS;
  const familyOf = new Map(FPC_FIELDS.map((f) => [f.key, f.family]));

  function rowHtml(f: FieldSpec): string {
    const tagCell = isIntegratorScope
      ? `<td data-tag="${f.key}" class="muted" style="min-width:80px">—</td>`
      : `<td class="muted" style="white-space:nowrap">default ${fpcBpsToMultiple(defaults[f.key])}×</td>`;
    const revertCell = isIntegratorScope
      ? `<td><button data-revert="${f.key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
      : `<td></td>`;
    const unit = f.family === 'SINGLE' ? '× per die' : '× return';
    return `<tr data-row="${f.key}" data-source="inherited">
      <td><strong>${f.label}</strong><div class="muted" style="font-size:12px;max-width:340px">${f.note}</div><div class="muted" style="font-size:12px"><code>${f.key}</code></div></td>
      <td style="white-space:nowrap"><input data-field="${f.key}" type="number" min="0.01" max="10000" step="0.01" style="width:90px" /> <span class="muted">${unit}</span></td>
      <td class="muted" data-hint="${f.key}">—</td>
      <td class="muted" style="white-space:nowrap">${((FPC_WINS[f.family] / FPC_OUTCOMES) * 100).toFixed(2)}%</td>
      <td data-rtp="${f.key}" style="white-space:nowrap">—</td>
      ${tagCell}
      ${revertCell}
    </tr>`;
  }

  const resetAllBtn = isIntegratorScope
    ? `<button id="fpcm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';
  const scopeNote = isIntegratorScope
    ? ' A save stores all three figures for this integrator, including the ones you did not change.'
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">One figure per bet family, entered as a multiple: every symbol is equally likely, so the bets of a family share it. Every figure moves in steps of 0.01× (${FPC_STEP_BPS} bps) — anything else takes wins off the ${FPC_STAKE_GRID}-credit stake grid and floors them down, so it is refused on save.${scopeNote} Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Per-family figures</h2>
      <table>
        <thead><tr><th>Family</th><th>Figure</th><th>Pays</th><th>P(win)</th><th>RTP</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="fpcm-rows">${FPC_FIELDS.map(rowHtml).join('')}</tbody>
      </table>
      <p class="muted" id="fpcm-headline" style="margin-top:10px">—</p>
      <div class="row" style="margin-top:14px">
        <button id="fpcm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="fpcm-save" class="primary">Propose change</button>
        <span id="fpcm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="fpcm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#fpcm-rows')!;
  const headlineEl = root.querySelector<HTMLElement>('#fpcm-headline')!;
  const msgEl = root.querySelector<HTMLElement>('#fpcm-msg')!;

  // Per-field inherited values for revert (integrator scope only).
  const inheritedValues = new Map<FpcField, number>();

  const inputs = new Map<FpcField, HTMLInputElement>();
  for (const key of FIELD_KEYS) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-field="${key}"]`);
    if (el) inputs.set(key, el);
  }

  function bpsOf(key: FpcField): number {
    return fpcMultipleToBps(inputs.get(key)?.value ?? '');
  }

  function refreshDerived(): void {
    let bestKey = FIELD_KEYS[0]!;
    let best = -Infinity;
    for (const key of FIELD_KEYS) {
      const family = familyOf.get(key)!;
      const bps = bpsOf(key);
      const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
      if (hintEl) {
        const warning =
          bps <= 0
            ? ' <span style="color:var(--error)">must be above zero</span>'
            : bps % FPC_STEP_BPS !== 0
              ? ` <span style="color:var(--error)">not a whole 0.01× — off the ${FPC_STAKE_GRID}-credit grid, wins round down</span>`
              : '';
        hintEl.innerHTML = `${family === 'SINGLE' ? singleReturnsHint(bps) : returnHint(bps)}${warning}`;
      }
      const rtp = fpcFamilyRtp(family, bps);
      const rtpEl = rowsEl.querySelector<HTMLElement>(`[data-rtp="${key}"]`);
      if (rtpEl) {
        const over = rtp > 1 ? ' <span style="color:var(--error)">over 100%!</span>' : '';
        rtpEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(2)}%</strong>${over}`;
      }
      if (rtp > best) {
        best = rtp;
        bestKey = key;
      }
    }
    // Headline is the BEST bet on the board, matching the RTP hero above
    // (admin-api takes the max across bet types, not an average).
    headlineEl.innerHTML =
      `Headline RTP (best bet on the board): <strong>${FPC_FIELDS.find((f) => f.key === bestKey)!.label}</strong> at ` +
      `<strong style="color:${rtpColor(best)}">${(best * 100).toFixed(2)}%</strong> — house edge ` +
      `<strong>${((1 - best) * 100).toFixed(2)}%</strong>.`;
  }

  function markRow(key: FpcField, source: 'override' | 'inherited'): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    const isOverride = source === 'override';
    if (row) row.dataset.source = source;
    if (tagEl) {
      tagEl.textContent = source;
      tagEl.style.color = isOverride ? 'var(--accent)' : 'var(--text-dim)';
      tagEl.style.fontWeight = isOverride ? '600' : '';
    }
    if (revertEl) revertEl.style.display = isOverride ? '' : 'none';
  }

  /** Seed rows from a full value (provider scope or reset). */
  function fillForm(figures: Partial<Record<FpcField, number>> | undefined): void {
    for (const [key, input] of inputs) input.value = fpcBpsToMultiple(figures?.[key] ?? defaults[key]);
    refreshDerived();
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    own: Partial<Record<FpcField, number>> | undefined,
    provider: Partial<Record<FpcField, number>> | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(
        key,
        own as Record<string, number> | undefined,
        provider as Record<string, number> | undefined,
        defaults[key],
      );
      input.value = fpcBpsToMultiple(classified.value);
      inheritedValues.set(key, provider?.[key] ?? defaults[key]);
      markRow(key, classified.source);
    }
    refreshDerived();
  }

  /**
   * Build the propose payload: all three figures, in both scopes. The schema
   * refuses a value with a figure missing, so the figures an integrator left
   * alone go in at the value they inherited.
   */
  function readForm(): FpcMultipliersValue {
    return {
      version: 1,
      singleWinningsPerDieBps: bpsOf('singleWinningsPerDieBps'),
      pairReturnBps: bpsOf('pairReturnBps'),
      longCardReturnBps: bpsOf('longCardReturnBps'),
    };
  }

  for (const [key, input] of inputs) {
    input.addEventListener('input', () => {
      if (isIntegratorScope) markRow(key, 'override');
      refreshDerived();
    });
  }

  if (isIntegratorScope) {
    for (const key of FIELD_KEYS) {
      rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`)?.addEventListener('click', () => {
        const input = inputs.get(key);
        if (input) input.value = fpcBpsToMultiple(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
        refreshDerived();
      });
    }

    root.querySelector<HTMLButtonElement>('#fpcm-reset-all')?.addEventListener('click', () => {
      for (const [key, input] of inputs) {
        input.value = fpcBpsToMultiple(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
      }
      refreshDerived();
      msgEl.textContent = 'All figures reset to provider/baked defaults (not saved).';
      msgEl.className = 'muted';
    });
  }

  /** Re-read the surface after a save/approve, respecting the current scope. */
  async function reload(): Promise<void> {
    if (isIntegratorScope) {
      const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
      fillFormInherited(
        pickFigures(inh.ownOverride.multipliers),
        pickFigures(inh.providerDefault.multipliers),
      );
      return;
    }
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    fillForm(pickFigures(value));
  }

  await reload();

  root.querySelector<HTMLButtonElement>('#fpcm-reset')!.addEventListener('click', () => {
    fillForm(defaults);
    if (isIntegratorScope) for (const key of FIELD_KEYS) markRow(key, 'override');
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#fpcm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = fishprawncrabMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#fpcm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
